// src/pages/AdminPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './AdminPage.css';

export default function AdminPage() {
  const navigate = useNavigate();
  const abortRef = useRef(null);

  // 비로그인 접근 차단
  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) {
      alert('로그인이 필요합니다.');
      navigate('/admin-login', { replace: true });
    }
  }, [navigate]);

  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [warning, setWarning] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    navigate('/admin-login', { replace: true });
  };

  const fetchData = async (deleted = false) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const url = deleted
      ? `${API_BASE_URL}/api/apply?deleted=true`
      : `${API_BASE_URL}/api/apply`;

    try {
      setLoading(true);
      const res = await fetch(url, { signal: controller.signal });
      if (res.status === 401) {
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        handleLogout();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const list = await res.json();
      const sorted = [...list].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      setData(sorted);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('불러오기 실패:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) {
      setWarning(true);
      setTimeout(() => setWarning(false), 2000);
      return;
    }
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/apply/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedRows),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setData((prev) => prev.filter((item) => !selectedRows.includes(item.id)));
      setSelectedRows([]);
    } catch (error) {
      console.error('삭제 실패', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleDownload = () => {
    // 같은 탭에서 다운로드
    window.location.href = `${API_BASE_URL}/api/apply/download/download`;
  };

  const handleToggleView = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    fetchData(next);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const ampm = date.getHours() < 12 ? '오전' : '오후';
    const hours = date.getHours() % 12 || 12;
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ${ampm} ${pad(hours)}시 ${pad(date.getMinutes())}분 ${pad(
      date.getSeconds()
    )}초`;
  };

  return (
    <div className="admin-container">
      <h2 className="admin-title">신청자 목록</h2>

      <div className="delete-button-container">
        <div className="button-row">
          <button className="toggle-button" onClick={handleToggleView}>
            {showDeleted ? '유효한 신청자 보기' : '삭제된 신청자 보기'}
          </button>

          {warning && <div className="warning-tooltip">삭제할 데이터를 체크하세요</div>}

          {!showDeleted && (
            <button className="delete-button" onClick={handleDelete}>
              삭제
            </button>
          )}
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th></th>
            <th>번호</th>
            <th>이름</th>
            <th>나이</th>
            <th>주소</th>
            <th>전화번호</th>
            <th>이메일</th>
            <th>취미</th>
            <th>지원동기</th>
            <th>신청일시</th>
          </tr>
        </thead>
        <tbody className={showDeleted ? 'gray-out-rows' : ''}>
          {loading ? (
            <tr>
              <td colSpan="10" className="error-message">불러오는 중…</td>
            </tr>
          ) : Array.isArray(data) && data.length > 0 ? (
            data.map((item, idx) => (
              <tr key={item.id}>
                <td>
                  {!showDeleted && (
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(item.id)}
                      onChange={() => handleCheckboxChange(item.id)}
                    />
                  )}
                </td>
                <td className="number-cell">{idx + 1}</td>
                <td>{item.name}</td>
                <td>{item.age}</td>
                <td>{item.address}</td>
                <td>{item.phone}</td>
                <td>{item.email}</td>
                <td>{item.hobby}</td>
                <td>{item.motive}</td>
                <td>{formatDate(item.createdAt)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" className="error-message">표시할 데이터가 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* 하단 바: 왼쪽 로그아웃, 오른쪽 다운로드 */}
      <div className="bottom-bar">
        <button className="logout-button" onClick={handleLogout}>
          로그아웃
        </button>
        <button className="download-button" onClick={handleDownload}>
          엑셀 다운로드
        </button>
      </div>
    </div>
  );
}